import {
  Box,
  Chip,
  Drawer,
  Divider,
  IconButton,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import { formatLabel } from "@/utils/format-label";
import { useActivity } from "@/hooks/use-activity";

const DrawerContent = styled(Box)(() => ({
  width: "34vw",
  minWidth: 360,
  maxWidth: 480,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 2.5, 2, 2.5),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
  flexShrink: 0,
}));

const Body = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(2.5),
  flex: 1,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: theme.spacing(1.25),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));

const MetaGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.4),
  minWidth: 0,
}));

const MetaLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.72rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.palette.text.disabled,
}));

const MetaValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  wordBreak: "break-word",
}));

const formatTimestamp = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const asNumber = Number(value);
  const date = Number.isNaN(asNumber)
    ? new Date(value)
    : new Date(asNumber * 1000);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => {
  const isSuccess = (status ?? "").toLowerCase() === "success";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.4,
        borderRadius: "6px",
        backgroundColor: isSuccess ? "#f0fdf4" : "#fef2f2",
        color: isSuccess ? "#15803d" : "#dc2626",
        fontSize: "0.8rem",
        fontWeight: 500,
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: isSuccess ? "#16a34a" : "#dc2626",
          flexShrink: 0,
        }}
      />
      {formatLabel(status) ?? "Unknown"}
    </Box>
  );
};

const Field = ({ label, value, mono }) => (
  <MetaItem>
    <MetaLabel>{label}</MetaLabel>
    <MetaValue sx={mono ? { fontFamily: "monospace", fontSize: "0.8rem" } : {}}>
      {value === null || value === undefined || value === "" ? "—" : value}
    </MetaValue>
  </MetaItem>
);

const LoadingState = () => (
  <Body>
    <Skeleton variant="rounded" height={80} />
    <Skeleton variant="rounded" height={120} />
    <Skeleton variant="rounded" height={120} />
  </Body>
);

export function ActivitySheet({ activityId, open, onClose }) {
  const { data: activity, isLoading } = useActivity(activityId, open);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlotProps={{ backdrop: { onClick: onClose } }}
      PaperProps={{ sx: { boxShadow: "-4px 0 24px rgba(0,0,0,0.08)" } }}
    >
      <DrawerContent>
        <Header>
          <IconWrapper>
            <HistoryOutlinedIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </IconWrapper>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={600}
              fontSize="1rem"
              noWrap
              title={activity?.action}
            >
              {activity?.action ?? "Activity Detail"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {activityId ? `ID: ${activityId}` : "—"}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "text.secondary", mt: -0.25, mr: -0.5, flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>

        <Divider />

        {isLoading || !activity ? (
          <LoadingState />
        ) : (
          <Body>
            {/* Overview */}
            <Box>
              <SectionLabel>Overview</SectionLabel>

              {activity.description && (
                <Typography
                  fontSize="0.85rem"
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.6 }}
                >
                  {activity.description}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <StatusBadge status={activity.status} />
                {activity.category && (
                  <Chip
                    label={activity.category}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      height: 28,
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      backgroundColor: "#eff6ff",
                      color: "#1e40af",
                    }}
                  />
                )}
              </Box>

              <MetaGrid>
                <Field label="Source Service" value={activity.sourceService} />
                <Field
                  label="Occurred At"
                  value={formatTimestamp(activity.occurredAt)}
                />
              </MetaGrid>
            </Box>

            <Divider />

            {/* Actor */}
            <Box>
              <SectionLabel>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                Actor
              </SectionLabel>
              <MetaGrid>
                <Field label="Name" value={activity.actorName} />
                <Field label="Email" value={activity.actorEmail} />
                <Field
                  label="Actor Type"
                  value={formatLabel(activity.actorType)}
                />
                <Field
                  label="User Type"
                  value={formatLabel(activity.userType)}
                />
                <Field label="Actor ID" value={activity.actorId} />
                <Field label="Customer ID" value={activity.customerId} />
              </MetaGrid>
            </Box>

            <Divider />

            {/* Request */}
            <Box>
              <SectionLabel>
                <LanguageOutlinedIcon sx={{ fontSize: 14 }} />
                Request
              </SectionLabel>
              <MetaGrid>
                <Field label="HTTP Method" value={activity.httpMethod} />
                <Field label="Status Code" value={activity.statusCode} />
                <Field label="IP Address" value={activity.ipAddress} mono />
                <Field label="Request ID" value={activity.requestId} mono />
                <Field label="Event ID" value={activity.eventId} mono />
                <Field label="User Agent" value={activity.userAgent} />
              </MetaGrid>
            </Box>

            {/* Metadata */}
            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
              <>
                <Divider />
                <Box>
                  <SectionLabel>
                    <DataObjectOutlinedIcon sx={{ fontSize: 14 }} />
                    Metadata
                  </SectionLabel>
                  <MetaGrid>
                    {Object.entries(activity.metadata).map(([key, value]) => (
                      <Field
                        key={key}
                        label={formatLabel(key)}
                        value={
                          value === null || value === undefined
                            ? "—"
                            : Array.isArray(value)
                              ? value.join(", ")
                              : String(value)
                        }
                      />
                    ))}
                  </MetaGrid>
                </Box>
              </>
            )}
          </Body>
        )}
      </DrawerContent>
    </Drawer>
  );
}
